'use client';

import { useState } from 'react';
import {
  ArrowLeft,
  LayoutDashboard,
  FolderKanban,
  ListTodo,
  Clock,
  Target,
} from 'lucide-react';
import { ProjectsDashboard } from './components/ProjectsDashboard';
import { ProjectListPage } from './components/ProjectList';
import { TaskListPage } from './components/TaskList';
import { TimesheetListPage } from './components/TimesheetList';

export type ProjectsPage =
  | 'dashboard'
  | 'projects'
  | 'tasks'
  | 'timesheets'
  | 'milestones';

interface ProjectsModuleProps {
  onBack?: () => void;
}

const menuItems: { id: ProjectsPage; label: string; icon: typeof LayoutDashboard }[] = [
  { id: 'dashboard', label: '대시보드', icon: LayoutDashboard },
  { id: 'projects', label: '프로젝트', icon: FolderKanban },
  { id: 'tasks', label: '작업', icon: ListTodo },
  { id: 'timesheets', label: '타임시트', icon: Clock },
  { id: 'milestones', label: '마일스톤', icon: Target },
];

export function ProjectsModule({ onBack }: ProjectsModuleProps) {
  const [currentPage, setCurrentPage] = useState<ProjectsPage>('dashboard');

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <ProjectsDashboard onNavigate={setCurrentPage} />;
      case 'projects':
        return <ProjectListPage />;
      case 'tasks':
        return <TaskListPage />;
      case 'timesheets':
        return <TimesheetListPage />;
      case 'milestones':
        return <MilestonesPlaceholder />;
      default:
        return <ProjectsDashboard onNavigate={setCurrentPage} />;
    }
  };

  return (
    <div className="flex flex-col h-full -m-6">
      {/* 상단 네비게이션 */}
      <div className="flex items-center gap-2 px-4 py-3 bg-card border-b border-border">
        {onBack && (
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-accent"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <h1 className="text-xl font-bold mr-6">프로젝트 관리</h1>

        {/* 탭 메뉴 */}
        <div className="flex items-center gap-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 페이지 컨텐츠 */}
      <div className="flex-1 overflow-auto p-6">
        {renderPage()}
      </div>
    </div>
  );
}

// 마일스톤 플레이스홀더
function MilestonesPlaceholder() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <Target className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-xl font-bold text-foreground mb-2">마일스톤 관리</h2>
        <p className="text-muted-foreground">마일스톤 페이지는 대시보드에서 확인하실 수 있습니다.</p>
      </div>
    </div>
  );
}
